import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, Download, TrendingUp, TrendingDown, 
  Building2, Calendar, PoundSterling, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

export default function LandlordStatements() {
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, statementsRes] = await Promise.all([
        fetch(`${API_URL}/api/statements/summary`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }),
        fetch(`${API_URL}/api/statements/monthly?year=${selectedYear}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (statementsRes.ok) {
        const data = await statementsRes.json();
        setStatements(data.statements || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatement = async (year, month) => {
    try {
      const response = await fetch(`${API_URL}/api/statements/monthly/${year}/${month}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedStatement(data);
      }
    } catch (error) {
      toast.error('Failed to load statement');
    }
  };

  const generateStatement = async (year, month) => {
    try {
      const response = await fetch(`${API_URL}/api/statements/generate/${year}/${month}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success('Statement generated');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to generate statement');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Financial Statements</h1>
          <p className="text-gray-600">View your monthly income, expenses, and payouts</p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.ytd?.income)}</p>
                  <p className="text-sm text-gray-500">YTD Income</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.ytd?.expenses)}</p>
                  <p className="text-sm text-gray-500">YTD Expenses</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <PoundSterling className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.ytd?.net)}</p>
                  <p className="text-sm text-gray-500">YTD Net</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.properties_count}</p>
                  <p className="text-sm text-gray-500">Properties</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Last Month Summary */}
        {summary?.last_month && (
          <Card className="p-6 mb-8">
            <h2 className="font-bold text-lg mb-4">Last Month: {summary.last_month.month} {summary.last_month.year}</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Income</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(summary.last_month.income)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expenses</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(summary.last_month.expenses)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Net</p>
                <p className={`text-xl font-bold ${summary.last_month.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.last_month.net)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Year Selection */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">Monthly Statements</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedYear(y => y - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium px-4">{selectedYear}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSelectedYear(y => y + 1)}
              disabled={selectedYear >= new Date().getFullYear()}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Months Grid */}
          <div className="lg:col-span-1">
            <div className="grid grid-cols-2 gap-2">
              {MONTHS.map((month, index) => {
                const monthNum = index + 1;
                const statement = statements.find(s => s.month === monthNum);
                const isPast = new Date(selectedYear, index) <= new Date();
                
                return (
                  <Card
                    key={month}
                    className={`p-3 cursor-pointer transition-all ${
                      selectedStatement?.month === monthNum && selectedStatement?.year === selectedYear
                        ? 'ring-2 ring-primary'
                        : 'hover:shadow-md'
                    } ${!isPast ? 'opacity-50' : ''}`}
                    onClick={() => isPast && fetchStatement(selectedYear, monthNum)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{month.slice(0, 3)}</p>
                        {statement ? (
                          <p className={`text-xs ${statement.summary?.net_amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(statement.summary?.net_amount)}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400">{isPast ? 'Generate' : '-'}</p>
                        )}
                      </div>
                      {statement && <FileText className="w-4 h-4 text-gray-400" />}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Statement Detail */}
          <div className="lg:col-span-2">
            {selectedStatement ? (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold">{selectedStatement.month_name} {selectedStatement.year}</h3>
                    <p className="text-sm text-gray-500">
                      {selectedStatement.properties_count} properties
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>

                {/* Income Section */}
                <div className="mb-6">
                  <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Income
                  </h4>
                  <div className="space-y-2">
                    {selectedStatement.income?.items?.map((item, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-sm">{item.description}</p>
                          <p className="text-xs text-gray-500">{item.property_address}</p>
                        </div>
                        <p className="font-medium text-green-600">{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 font-bold">
                      <span>Total Income</span>
                      <span className="text-green-600">{formatCurrency(selectedStatement.income?.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses Section */}
                <div className="mb-6">
                  <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Expenses
                  </h4>
                  <div className="space-y-2">
                    {selectedStatement.expenses?.items?.map((item, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-sm">{item.description}</p>
                          <p className="text-xs text-gray-500">{item.property_address}</p>
                        </div>
                        <p className="font-medium text-red-600">-{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 font-bold">
                      <span>Total Expenses</span>
                      <span className="text-red-600">-{formatCurrency(selectedStatement.expenses?.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500">Total Income</p>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(selectedStatement.summary?.total_income)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Expenses</p>
                      <p className="text-lg font-bold text-red-600">
                        {formatCurrency(selectedStatement.summary?.total_expenses)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Net Amount</p>
                      <p className={`text-lg font-bold ${
                        selectedStatement.summary?.net_amount >= 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(selectedStatement.summary?.net_amount)}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Select a month to view statement details</p>
                <p className="text-sm text-gray-400 mt-2">
                  Click on any past month to generate or view your statement
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
